import { message } from 'antd';
import { RcFile, UploadChangeParam, UploadProps } from 'antd/lib/upload';
import {
  FileOutput,
  UserAttachmentOutput,
  useDetachUserFileMutation,
  useGetUserAttachmentsQuery,
} from 'gql/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { envServices } from 'utils/EnvServices';
import { fetchWithAuth } from 'utils/fetchWithAuth';
import { getAuthHeaders } from 'utils/getAuthHeaders';

import { UserAttachmentsTableProps } from './types';

export const useUserAttachmentsTable = (props: UserAttachmentsTableProps) => {
  const { userId } = props;
  const [showUploadList, setShowUploadedList] = useState(true);
  const { t } = useTranslation('owner.OwnerAttachmentsTable');
  const getUserAttachmentsQuery = useGetUserAttachmentsQuery({
    variables: {
      input: { userId },
    },
  });
  const [detachUserFile, detachUserFileMutation] = useDetachUserFileMutation({
    refetchQueries: ['getUserAttachments'],
    onCompleted: () => {
      message.success(t('fileDeleted'));
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const downloadFileHandler = async (file: FileOutput) => {
    const response = await fetchWithAuth(
      `${envServices.get('SERVER_URI')}/user/attachment/${file.id}`,
      {
        method: 'GET',
      }
    );
    const blob = await response.blob();
    const url = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${file?.filename}`);
    document.body.appendChild(link);
    link.click();
    link?.parentNode?.removeChild(link);
  };

  const isLoading = [getUserAttachmentsQuery, detachUserFileMutation].some(
    ({ loading }) => loading
  );
  const ownerAttachments: UserAttachmentOutput[] =
    getUserAttachmentsQuery.data?.getUserAttachments || [];

  const onChangeUploadFile = (uploadParams: UploadChangeParam) => {
    setShowUploadedList(true);
    if (uploadParams.file.status === 'done') {
      message.success(t('fileUploaded'));
      setShowUploadedList(false);
      getUserAttachmentsQuery.refetch({
        input: {
          userId,
        },
      });
    }
  };

  const detachFileHandler = (fileId: string) => {
    detachUserFile({
      variables: {
        input: {
          fileId,
          id: userId,
        },
      },
    });
  };

  const beforeUpload = (file: RcFile) => {
    const isLessThan5Mb = file.size / 1024 / 1024 < 5;
    if (!isLessThan5Mb) {
      message.error(t('fileMustBeLessThan', { size: 5 }));
      (file as UploadChangeParam['file']).status = 'error';
    }

    return isLessThan5Mb;
  };

  const uploadAttachmentProps: UploadProps = {
    showUploadList,
    name: 'file',
    action: `${envServices.get('SERVER_URI')}/user/attachment/upload`,
    headers: getAuthHeaders(),
    maxCount: 1,
    onChange: onChangeUploadFile,
    beforeUpload,
    data: {
      userId,
    },
  };

  return {
    isLoading,
    ownerAttachments,
    uploadAttachmentProps,
    downloadFileHandler,
    detachFileHandler,
  };
};
